// tardiaBono.js — cancelación tardía de una práctica pagada con BONO: qué pasa con la sesión y con el cargo
// (9/10/2026, Náyade y Poveda). Ficha: docs/features/autoescuela-tardia-con-bono.md
//
// c_autoescuela_config.late_cancellation.con_bono:
//   "cobrar"   (de fábrica, lo de siempre) → se le devuelve la sesión y se le cobra el importe.
//   "pierde"   → pierde la clase: se descuenta del bono y no hay cargo en euros (si el hueco se rellena, se le devuelve).
//   "devolver" → se le devuelve la sesión, sin cargo.
//   "pierde_siempre" (14/10/2026, Arribas) → como «pierde», pero la sesión NO se devuelve aunque otro alumno coja el
//              hueco (el hueco se sigue ofreciendo). El aviso es el mismo (no promete devolución).
//
// ESPEJO del backend (lo fija __tests__/tardiaBono.mirror.test.js):
//   - POLITICAS_BONO / politicaBono ← aws_lambdas/_shared_autoescuela/tardiaBono.js
//   - CUERPO_BONO                   ← admin_scripts/add_autoescuela_examen_propuesta_y_bono_templates.js (practica_cancelacion_bono_trial)
//
// PURO; solo importa el catálogo de plantillas (para la a medida). COPIA POR VALOR (fichero idéntico) en
// apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/tardiaBono.js — un test del simulador los compara.

import { vistaPlantilla } from "./plantillasAutoescuela";

export const POLITICAS_BONO = ["cobrar", "pierde", "devolver", "pierde_siempre"];
export const POLITICA_BONO_DEFECTO = "cobrar";
// Práctica SIN bono (19/10/2026, Carian): "normal" (de fábrica, «Solo avisar» / «Registrar y cobrar» como siempre) o
// "pierde" (pierde la clase: cuenta como dada, sin cargo, y le llega practica_cancelacion_pierde_trial). ESPEJO de
// POLITICAS_SIN_BONO de aws_lambdas/_shared_autoescuela/tardiaBono.js.
export const POLITICAS_SIN_BONO = ["normal", "pierde"];
export const POLITICA_SIN_BONO_DEFECTO = "normal";

/** La política para las prácticas SIN bono (lo que no vale = «normal»). */
export function politicaSinBono(lateCancellation) {
  const v = lateCancellation && lateCancellation.sin_bono;
  return POLITICAS_SIN_BONO.includes(v) ? v : POLITICA_SIN_BONO_DEFECTO;
}

/** La política guardada (lo que no vale = la de fábrica). */
export function politicaBono(lateCancellation) {
  const v = lateCancellation && lateCancellation.con_bono;
  return POLITICAS_BONO.includes(v) ? v : POLITICA_BONO_DEFECTO;
}

/** ¿La clase cuenta como dada (sesión descontada)? «pierde» y «pierde_siempre» (espejo del backend). */
export function pierdeSesion(pol) {
  return pol === "pierde" || pol === "pierde_siempre";
}

/** Cuerpo de practica_cancelacion_bono_trial (lo que recibe el alumno con «Pierde la clase»). */
export const CUERPO_BONO = {
  es: "📩 *{{1}}* te informa:\n\nHola {{2}},\n\nTu práctica del {{3}} a las {{4}} se ha cancelado con menos de {{5}} horas de antelación.\n\nSegún las condiciones del centro, la clase se descuenta de tu bono y no se añade ningún cargo a tu cuenta. Puedes consultarlo con nosotros por este chat.",
  ca: "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva pràctica del {{3}} a les {{4}} s'ha cancel·lat amb menys de {{5}} hores d'antelació.\n\nSegons les condicions del centre, la classe es descompta del teu bo i no s'afegeix cap càrrec al teu compte. Pots consultar-ho amb nosaltres per aquest xat.",
};

/** Cuerpo de practica_cancelacion_pierde_trial («Pierde la clase» SIN bono). ESPEJO de
 * admin_scripts/add_autoescuela_cancelacion_pierde_template.js. */
export const CUERPO_PIERDE = {
  es: "📩 *{{1}}* te informa:\n\nHola {{2}},\n\nTu práctica del {{3}} a las {{4}} se ha cancelado con menos de {{5}} horas de antelación.\n\nSegún las condiciones del centro, la clase se pierde y cuenta como realizada. Puedes consultarlo con nosotros por este chat.",
  ca: "📩 *{{1}}* t'informa:\n\nHola {{2}},\n\nLa teva pràctica del {{3}} a les {{4}} s'ha cancel·lat amb menys de {{5}} hores d'antelació.\n\nSegons les condicions del centre, la classe es perd i compta com a feta. Pots consultar-ho amb nosaltres per aquest xat.",
};

/** El aviso de «Pierde la clase» SIN bono rellenado (con la a medida de la empresa, si la tiene). */
export function textoClasePerdida({ negocio, nombre, fecha, hora, horas, idioma, aMedida = null } = {}) {
  const vals = [negocio || "Tu autoescuela", nombre, fecha, hora, String(horas)];
  if (aMedida && Object.keys(aMedida).length) {
    const v = vistaPlantilla("practica_cancelacion_pierde_trial", { idioma: idioma === "ca" ? "ca" : "es", vals, aMedida });
    if (v) return v.message;
  }
  return CUERPO_PIERDE[idioma === "ca" ? "ca" : "es"].replace(/\{\{(\d)\}\}/g, (_, n) => vals[Number(n) - 1]);
}

/**
 * Qué pasa cuando un alumno SIN bono cancela tarde, en llano, para la tarjeta: { alumno, cuenta } ({ k, d, o }; clave
 * msgs:page.tardiaBono.<k>). Con «normal», lo de «Solo avisar» / «Registrar y cobrar».
 */
export function efectoSinBono(politica, { cobra = true, importe = "" } = {}) {
  const n = (k, d, o) => (o ? { k, d, o } : { k, d });
  if (politica === "pierde") {
    return {
      alumno: n("sbAlumnoPierde", "Le llega UN solo WhatsApp (en vez del aviso de cancelación): «la clase se pierde y cuenta como realizada». Sin ningún texto de cargo."),
      cuenta: n("sbCuentaPierde", "La práctica queda como clase perdida (cuenta como dada) y no se le apunta nada en su cuenta, aunque otro alumno se quede el hueco."),
    };
  }
  return cobra
    ? {
      alumno: n("sbAlumnoCobrar", "Le llega el aviso de cancelación y, si nadie coge el hueco, «se ha añadido un cargo de {{importe}} a tu cuenta».", { importe: importe || "X €" }),
      cuenta: n("sbCuentaCobrar", "Se le apunta el cargo en su cuenta (salvo que otro alumno se quede el hueco)."),
    }
    : {
      alumno: n("sbAlumnoAvisar", "Le llega solo el aviso de cancelación de siempre."),
      cuenta: n("sbCuentaAvisar", "Queda registrada como cancelación tardía, sin cargo."),
    };
}

/** El aviso de «Pierde la clase» rellenado. Solo existe por el número compartido (la _trial). */
export function textoSesionDescontada({ negocio, nombre, fecha, hora, horas, idioma, aMedida = null } = {}) {
  const vals = [negocio || "Tu autoescuela", nombre, fecha, hora, String(horas)];
  // La a medida de la autoescuela (9/10/2026): la misma que manda c_sendTemplate (catálogo de plantillas).
  if (aMedida && Object.keys(aMedida).length) {
    const v = vistaPlantilla("practica_cancelacion_bono_trial", { idioma: idioma === "ca" ? "ca" : "es", vals, aMedida });
    if (v) return v.message;
  }
  return CUERPO_BONO[idioma === "ca" ? "ca" : "es"].replace(/\{\{(\d)\}\}/g, (_, n) => vals[Number(n) - 1]);
}

/**
 * Qué pasa cuando un alumno CON BONO cancela tarde, en llano, para la tarjeta: { alumno, bono }, cada uno
 * { k, d, o } (clave de traducción msgs:page.tardiaBono.<k>, texto en castellano y variables).
 * `cobra`: la tardía está en «cobrar» con importe (si no, «cobrar» no apunta ningún cargo).
 */
export function efectoConBono(politica, { cobra = true, importe = "" } = {}) {
  const n = (k, d, o) => (o ? { k, d, o } : { k, d });
  if (politica === "pierde") {
    return {
      alumno: n("alumnoPierde", "Le llega UN solo WhatsApp (en vez del aviso de cancelación): «la clase se descuenta de tu bono y no se añade ningún cargo a tu cuenta»."),
      bono: n("bonoPierde", "Pierde esa sesión del bono y no se le cobra nada más. Si otro alumno se queda el hueco, se le devuelve la sesión."),
    };
  }
  if (politica === "pierde_siempre") {
    return {
      alumno: n("alumnoPierde", "Le llega UN solo WhatsApp (en vez del aviso de cancelación): «la clase se descuenta de tu bono y no se añade ningún cargo a tu cuenta»."),
      bono: n("bonoPierdeSiempre", "Pierde esa sesión del bono y no se le cobra nada más, AUNQUE otro alumno se quede el hueco: cuenta como clase dada. El hueco se sigue ofreciendo."),
    };
  }
  if (politica === "devolver") {
    return {
      alumno: n("alumnoDevolver", "No le llega ningún mensaje más (solo el aviso de que la práctica se ha cancelado)."),
      bono: n("bonoDevolver", "Se le devuelve la sesión al bono y no se le cobra nada. Queda apuntada como cancelación tardía."),
    };
  }
  return cobra
    ? {
      alumno: n("alumnoCobrar", "Le llega un WhatsApp: «se ha añadido un cargo de {{importe}} a tu cuenta».", { importe: importe || "X €" }),
      bono: n("bonoCobrar", "Se le devuelve la sesión al bono y además se le apunta el cargo en su cuenta."),
    }
    : {
      alumno: n("alumnoCobrarSinImporte", "No le llega ningún mensaje más."),
      bono: n("bonoCobrarSinImporte", "Se le devuelve la sesión al bono. Con «Solo avisar» no se apunta ningún cargo."),
    };
}
